// Phase 9.2 — PO history list (default landing for the "Purchase Orders"
// sub-nav item).

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API, detailFromError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  POStatusPill, PoFormatBadge, PO_STATUS_ORDER, fmtINR,
} from "./poStatusMeta";

const PAGE_SIZE = 50;

export default function FnbPoPurchaseOrders() {
  const [status, setStatus] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/fnb/vendors"),
      api.get("/fnb/locations"),
    ]).then(([v, l]) => {
      if (cancelled) return;
      setVendors(v.data);
      setLocations(l.data.filter((x) => x.active !== false && x.is_po_target));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset });
      if (status) params.set("status", status);
      if (vendorFilter) params.set("vendor", vendorFilter);
      if (locationFilter) params.set("location", locationFilter);
      const res = await api.get(`/fnb/pos?${params}`);
      setData(res.data);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setLoading(false);
    }
  }, [status, vendorFilter, locationFilter, offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); }, [status, vendorFilter, locationFilter]);

  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <div className="space-y-6" data-testid="fnb-po-purchase-orders">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="pos-heading">
            Purchase Orders
          </h1>
          <p className="text-sm text-muted-foreground">
            Full history — draft, released, received, cancelled.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm" data-testid="new-po-btn">
            <Link to="/tools/fnb-po/pos/new">+ New PO</Link>
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to="/tools/fnb-po">← Dashboard</Link>
          </Button>
        </div>
      </header>

      {/* Filters */}
      <Card className="border-border/70">
        <CardContent className="pt-6 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Status</span>
            <div className="flex rounded-md border border-border/70 overflow-hidden text-xs">
              <button
                className={`px-2.5 py-1 transition ${
                  status === "" ? "bg-foreground text-background" : "hover:bg-muted"
                }`}
                onClick={() => setStatus("")}
                data-testid="filter-status-all"
              >
                All
              </button>
              {PO_STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  className={`px-2.5 py-1 border-l border-border/70 transition ${
                    status === s ? "bg-foreground text-background" : "hover:bg-muted"
                  }`}
                  onClick={() => setStatus(s)}
                  data-testid={`filter-status-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <Select value={vendorFilter} onValueChange={(v) => setVendorFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-56 text-xs" data-testid="filter-vendor">
              <SelectValue placeholder="Any vendor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__" className="text-xs">Any vendor</SelectItem>
              {vendors.map((v) => (
                <SelectItem key={v.vendor_id} value={v.vendor_id} className="text-xs">
                  {v.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={locationFilter} onValueChange={(v) => setLocationFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-56 text-xs" data-testid="filter-location">
              <SelectValue placeholder="Any location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__" className="text-xs">Any location</SelectItem>
              {locations.map((l) => (
                <SelectItem key={l.location_id} value={l.location_id} className="text-xs">
                  {l.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between text-sm">
            <div><b>{total}</b> PO{total === 1 ? "" : "s"}</div>
            <div className="text-xs text-muted-foreground">
              {items.length ? `Showing ${offset + 1}–${offset + items.length}` : ""}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && !data ? (
            <div className="text-sm text-muted-foreground" data-testid="pos-loading">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center" data-testid="pos-empty">
              No POs yet. Head to the Reorder page and generate one from your critical items.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="pos-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>PO #</TableHead>
                    <TableHead>Format</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Destinations</TableHead>
                    <TableHead className="text-right">Lines</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => (
                    <TableRow key={r.po_id} data-testid={`po-row-${r.po_id}`}>
                      <TableCell className="font-mono text-xs">
                        <Link
                          to={`/tools/fnb-po/pos/${r.po_id}`}
                          className="text-primary underline underline-offset-4"
                          data-testid={`po-link-${r.po_id}`}
                        >
                          {r.po_number}
                        </Link>
                      </TableCell>
                      <TableCell><PoFormatBadge format={r.format} /></TableCell>
                      <TableCell className="text-sm">
                        {r.vendor_snapshot?.name || r.vendor_id}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[16rem] truncate">
                        {(r.destination_locations || []).join(", ")}
                      </TableCell>
                      <TableCell className="text-right text-sm">{r.totals?.line_count || 0}</TableCell>
                      <TableCell className="text-right font-medium">{fmtINR(r.totals?.total_amount)}</TableCell>
                      <TableCell><POStatusPill status={r.status} testid={`po-status-${r.po_id}`} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <a
                          href={`${API}/fnb/pos/${r.po_id}/download`}
                          className="text-xs text-primary underline underline-offset-4"
                          data-testid={`po-download-${r.po_id}`}
                        >
                          ↓ Excel
                        </a>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between pt-4">
                <div className="text-xs text-muted-foreground">
                  Page {Math.floor(offset / PAGE_SIZE) + 1} of{" "}
                  {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    data-testid="pos-prev"
                  >← Prev</Button>
                  <Button
                    variant="outline" size="sm"
                    disabled={offset + items.length >= total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    data-testid="pos-next"
                  >Next →</Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
